import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Lock,
  ArrowRight,
  Plus,
  Building2,
  ExternalLink,
  CheckCircle2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { getVendorStatus } from '../../services/subscriptionService';
import { useToastContext } from '../../contexts/ToastContext';
import {
  useVendorLocations,
  useCreateVendorLocation,
  useUpdateVendorLocation,
  useDeleteVendorLocation,
  useToggleVendorLocationStatus,
} from '../../services/vendorLocationService';
import LocationCard from '../../components/vendor/locations/LocationCard';
import LocationFormModal from '../../components/vendor/locations/LocationFormModal';

const VendorMultiLocation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToastContext();
  const { hasActiveSubscription, isChecking } = useSubscriptionStatus();
  const [hasFeature, setHasFeature] = useState(false);
  const [featureLoading, setFeatureLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const { data: locationsResponse, isLoading: locationsLoading } = useVendorLocations({
    enabled: hasActiveSubscription === true && hasFeature,
  });

  const createMutation = useCreateVendorLocation({
    onSuccess: () => {
      showSuccess('Location added', 'Your new service area is saved.');
      closeModal();
    },
    onError: (err) => {
      showError('Could not add location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const updateMutation = useUpdateVendorLocation({
    onSuccess: () => {
      showSuccess('Location updated', 'Changes saved successfully.');
      closeModal();
    },
    onError: (err) => {
      showError('Could not update location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const deleteMutation = useDeleteVendorLocation({
    onSuccess: () => showSuccess('Location removed', 'Service area deleted.'),
    onError: (err) => {
      showError('Could not remove location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const toggleMutation = useToggleVendorLocationStatus({
    onSuccess: (res) => {
      showSuccess('Status updated', res?.message || 'Location status changed.');
    },
    onError: (err) => {
      showError('Could not update status', err?.response?.data?.message || 'Please try again.');
    },
  });

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (hasActiveSubscription === false) {
      navigate('/vendor/subscription', { replace: true });
      return;
    }
    if (hasActiveSubscription !== true) return;

    const fetchStatus = async () => {
      try {
        const response = await getVendorStatus();
        const features = response?.data?.current_subscription?.features || {};
        setHasFeature(!!features.feature_multi_location);
      } catch (err) {
        console.error('Error fetching subscription:', err);
      } finally {
        setFeatureLoading(false);
      }
    };
    fetchStatus();
  }, [user?.user_type, hasActiveSubscription, navigate]);

  const pageData = locationsResponse?.data || {};
  const locations = pageData.locations || [];
  const primary = pageData.primary_location || {};
  const stats = pageData.statistics || {};
  const maxLocations = pageData.max_locations || 25;
  const canAddMore = (stats.remaining ?? maxLocations) > 0;

  const closeModal = () => {
    setModalOpen(false);
    setEditingLocation(null);
  };

  const openCreate = () => {
    setEditingLocation(null);
    setModalOpen(true);
  };

  const openEdit = (location) => {
    setEditingLocation(location);
    setModalOpen(true);
  };

  const handleSubmit = (payload) => {
    if (editingLocation) {
      updateMutation.mutate({ id: editingLocation.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (location) => {
    if (!window.confirm(`Remove "${location.area_name}" from your service areas?`)) return;
    setDeletingId(location.id);
    deleteMutation.mutate(location.id, { onSettled: () => setDeletingId(null) });
  };

  const handleToggle = (location) => {
    setTogglingId(location.id);
    toggleMutation.mutate(location.id, { onSettled: () => setTogglingId(null) });
  };

  const primaryLabel = [primary.city, primary.state, primary.country].filter(Boolean).join(', ');
  const publicProfileUrl = pageData.profile_id ? `/vendor/${pageData.profile_id}` : null;

  if (isChecking || featureLoading || (hasFeature && locationsLoading) || hasActiveSubscription !== true) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black" />
      </div>
    );
  }

  if (!hasFeature) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Multi-Location</h1>
          <p className="text-gray-600 mb-6">
            This feature is not included in your current plan. Upgrade to manage multiple service locations and reach clients across different areas.
          </p>
          <Link
            to="/vendor/subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            View Plans
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 bg-black rounded-xl flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Multi-Location</h1>
                <p className="text-sm text-gray-600">
                  Manage where you operate — shown on your public vendor profile
                </p>
              </div>
            </div>
            {stats.active > 0 && (
              <p className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mt-2">
                <CheckCircle2 className="w-4 h-4" />
                {stats.active} active service {stats.active === 1 ? 'area' : 'areas'} live on your profile
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {publicProfileUrl && (
              <Link
                to={publicProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              >
                <ExternalLink className="w-4 h-4" />
                View public page
              </Link>
            )}
            <button
              type="button"
              onClick={openCreate}
              disabled={!canAddMore}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-black text-white hover:bg-gray-800 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add location
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total areas', value: stats.total ?? 0 },
            { label: 'Active', value: stats.active ?? 0 },
            { label: 'Inactive', value: stats.inactive ?? 0 },
            { label: 'Slots left', value: stats.remaining ?? maxLocations },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-gray-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h2 className="text-base font-semibold text-gray-900">Primary business location</h2>
                <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-800">
                  HQ
                </span>
              </div>
              <p className="text-sm font-medium text-gray-800">{primary.business_name || 'Your business'}</p>
              {primaryLabel && <p className="text-sm text-gray-600 mt-0.5">{primaryLabel}</p>}
              {primary.address && <p className="text-xs text-gray-500 mt-1">{primary.address}</p>}
              <Link
                to="/vendor/profile"
                className="inline-block text-xs text-gray-600 underline hover:text-black mt-2"
              >
                Edit in vendor profile
              </Link>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Additional service areas</h2>
            {!canAddMore && (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                Maximum {maxLocations} locations reached
              </span>
            )}
          </div>

          {locations.length === 0 ? (
            <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center">
              <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">No extra locations yet</h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto mb-4">
                Add neighborhoods, cities, or regions you serve beyond your primary address. Clients will see these on your public profile.
              </p>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-black text-white hover:bg-gray-800"
              >
                <Plus className="w-4 h-4" />
                Add your first location
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {locations.map((location) => (
                <LocationCard
                  key={location.id}
                  location={location}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                  isToggling={togglingId === location.id}
                  isDeleting={deletingId === location.id}
                />
              ))}
            </div>
          )}
        </section>

        <p className="text-xs text-gray-500 text-center pb-4">
          Active locations appear under &quot;Service areas&quot; on your public vendor page. Travel fees are informational for clients.
        </p>
      </div>

      <LocationFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialLocation={editingLocation}
        isSaving={createMutation.isPending || updateMutation.isPending}
        primaryLocation={primary}
      />
    </div>
  );
};

export default VendorMultiLocation;
